import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { TrendingUp } from 'lucide-react';
import { C } from '../data/constants.js';
import type { ProjectionPoint } from '../data/constants.js';
import { AnimatedNumber } from './AnimatedNumber.js';
import { EmptyState } from './EmptyState.js';

export interface PredictedSavingsProps {
  projectedAnnual?: number;
  data?: ProjectionPoint[];
}

export function PredictedSavings({
  projectedAnnual,
  data,
}: PredictedSavingsProps): React.ReactElement {
  if (projectedAnnual == null || !data || data.length === 0) {
    return (
      <EmptyState
        icon={TrendingUp}
        title="Projected Savings Not Available"
        message="Savings projections will appear once waste and dead time data is being collected."
      />
    );
  }

  return (
    <div
      style={{
        background: `linear-gradient(135deg, ${C.accentDim} 0%, rgba(0,207,160,0.01) 100%)`,
        border: `1px solid ${C.accentBorder}`,
        borderRadius: 16,
        padding: '24px',
        animation: 'insights-fadeSlideUp 0.5s ease 200ms both',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <TrendingUp size={18} color={C.accent} />
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: C.accent,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Projected Value Identified
            </span>
          </div>
          <div style={{ fontSize: 11, color: C.textDim }}>
            Based on current trajectory with 8% month-over-month improvement
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: C.textDim }}>12-Month Projection</div>
          <div
            style={{
              fontSize: 32,
              fontWeight: 700,
              color: '#fff',
              fontFamily: "'Space Mono', monospace",
              letterSpacing: -1,
            }}
          >
            <AnimatedNumber value={projectedAnnual} prefix="$" />
          </div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <ComposedChart data={data}>
          <defs>
            <linearGradient id="gradProj" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={C.accent} stopOpacity={0.15} />
              <stop offset="100%" stopColor={C.accent} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 10, fill: C.textFaint }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 10, fill: C.textFaint }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`}
          />
          <Tooltip
            contentStyle={{
              background: '#14161A',
              border: `1px solid ${C.cardBorder}`,
              borderRadius: 10,
              fontSize: 12,
            }}
            formatter={(v) =>
              typeof v === 'number' ? ([`$${v.toLocaleString()}`, ''] as any) : (['-', ''] as any)
            }
          />
          <Area
            type="monotone"
            dataKey="projected"
            stroke={C.accent}
            strokeWidth={2}
            strokeDasharray="6 4"
            fill="url(#gradProj)"
            name="Projected"
          />
          <Line
            type="monotone"
            dataKey="actual"
            stroke="#fff"
            strokeWidth={2.5}
            dot={{ fill: '#fff', r: 4 }}
            name="Actual"
            connectNulls={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <div style={{ display: 'flex', gap: 20, marginTop: 8, fontSize: 11, color: C.textDim }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 16, height: 2, background: '#fff', borderRadius: 1 }} /> Actual
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 16, height: 2, background: C.accent, borderRadius: 1 }} /> Projected
        </span>
      </div>
    </div>
  );
}
